/** Status and contract chips shared by the partner list and the partner page.
 *  Never color alone: every chip carries its text. */
import { Chip, Tooltip } from '@mui/material';

import {
  GATE_REFUSAL_LABELS,
  PARTNER_STATUS_LABELS,
  type Partner,
  type PartnerStatus,
} from '@/api/partners';
import { formatDate } from '@/utils/format';

const STATUS_COLOR: Record<PartnerStatus, 'success' | 'warning' | 'default'> = {
  active: 'success',
  suspended: 'warning',
  terminated: 'default',
};

export function PartnerStatusChip({ status }: { status: PartnerStatus }) {
  return <Chip size="small" variant="outlined" color={STATUS_COLOR[status]} label={PARTNER_STATUS_LABELS[status]} />;
}

/** The gate in one chip: "In force until <date>", or why a key would be refused. */
export function PartnerContractChip({ partner }: { partner: Pick<Partner, 'gate_refusal' | 'contract_term_end' | 'contract_recorded'> }) {
  const until = partner.contract_term_end ? formatDate(partner.contract_term_end) : null;
  if (!partner.gate_refusal) {
    return (
      <Tooltip title="The contract is recorded and in force: the partner may hold developer keys.">
        <Chip size="small" color="success" variant="outlined" label={until ? `In force until ${until}` : 'In force'} />
      </Tooltip>
    );
  }
  const label = GATE_REFUSAL_LABELS[partner.gate_refusal] ?? partner.gate_refusal;
  return (
    <Tooltip title="Developer keys are refused until this is resolved.">
      <Chip
        size="small"
        color={partner.gate_refusal === 'partner_contract_missing' ? 'warning' : 'default'}
        variant="outlined"
        label={label}
      />
    </Tooltip>
  );
}
